import {
  type InfiniteData,
  type QueryFunction,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import type { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import Logger from '~/services/Logger'
import { FaxDirectionFilter, QueryCancellation } from '~/types/ApiRequestTypes'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'
import {
  isClientError,
  isMarkFaxAsReadResponse,
} from '~/utils/mangoApiClient/mangoApiClient'
import { showErrorToast } from '~/utils/toast'

import type {
  ApiError,
  Fax,
  FaxInstance,
  PaginatedApiResult,
} from '@mangovoice/types'

export type FaxSearch = {
  offset?: number
  search?: string
  direction?: FaxDirectionFilter
  extension?: number
  unread?: boolean
  options?: Omit<
    UseInfiniteQueryOptions<PaginatedApiResult<Fax>, ApiError>,
    'queryKey'
  >
}

export enum FaxesQueryKeys {
  FAXES = 'faxes',
  FAX = 'fax',
}

export type UnreadCountQueryKeys = {
  byExtension: QueryKey
  total: QueryKey
}

export type UnreadExtensionCounts = Map<number, number>

/** Confirmed 750ms is too quick to be effective. */
const autoReadRehydrateTimeoutMs = 1000
const fiveMinutesMs = 300_000

const logger = new Logger('faxQueriesLogger')

export const useGetFax = (
  faxId: number | null,
  options?: Omit<UseQueryOptions<FaxInstance, ApiError>, 'queryKey'>,
) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  const getQueryKeys = () => [FaxesQueryKeys.FAX, faxId]

  const tryCancelQueries: QueryCancellation = () => {
    queryClient.cancelQueries({
      queryKey: getQueryKeys(),
    })
  }

  const query = useQuery<FaxInstance, ApiError>({
    queryKey: getQueryKeys(),
    queryFn: async ({ signal }) => {
      if (!faxId || faxId === 0) {
        return Promise.reject({ message: 'faxId is required' })
      }
      const { ok, data, status } = await api.getFax(faxId, signal)

      if (!ok) {
        const error = {
          ...data,
          status,
        }

        return Promise.reject(error)
      }

      return data!
    },
    ...options,
  })

  return {
    ...query,
    tryCancelQueries,
  }
}

interface UseSendMultipageFaxOptions {
  onError?: (errorMessage?: string) => void
  onSuccess?: () => void
}
interface SendMultipageFaxParams {
  emailConfirmations: string[]
  files: File[]
  fromPhoneNumber: string
  onProgress: (progress: ProgressEvent) => void
  pbxId: number
  toPhoneNumber: string
}
export const useSendFax = (options?: UseSendMultipageFaxOptions) => {
  const [cancelToken, setCancelToken] = useState<CancelTokenSource | null>(null)
  const api = useMangoApi()
  const mutation = useMutation({
    mutationFn: async ({
      emailConfirmations,
      files,
      fromPhoneNumber,
      onProgress,
      pbxId,
      toPhoneNumber,
    }: SendMultipageFaxParams) => {
      if (!pbxId) {
        return Promise.reject('No extension id provided.')
      }

      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))
      formData.append('from_pbx_id', pbxId.toString())
      formData.append('from_phone_number', fromPhoneNumber)
      formData.append('to_phone_number', toPhoneNumber)
      formData.append('is_multi_page', String(true)) // Vestigial. Remove once the API doesn't rely on it.

      if (emailConfirmations && emailConfirmations.length > 0) {
        formData.append('email_confirmation_to', emailConfirmations.join(','))
      }

      const { ok } = await api.sendFax({
        formData,
        onUploadProgress: onProgress,
        onRequestStarted: setCancelToken,
      })

      if (!ok) {
        return Promise.reject('Failed to send fax.')
      }

      return Promise.resolve()
    },
    onSuccess: () => {
      setCancelToken(null)
      if (options?.onSuccess) {
        options.onSuccess()
      }
    },
    onError: (error: ApiError) => {
      setCancelToken(null)

      if (options?.onError) {
        options.onError(error.message)
      }
    },
  })
  const cancelRequest = () => cancelToken?.cancel()

  return { ...mutation, cancelRequest }
}

interface UseDeleteFaxParams {
  onSuccess: () => void
  onError?: (error: ApiError) => void
}
export const useDeleteFax = ({ onSuccess, onError }: UseDeleteFaxParams) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: (faxId: number) => api.deleteFax(faxId),
    onSuccess,
    onError,
  })
}

interface UseResendFaxParams {
  onSuccess: () => void
  onError: (error: ApiError) => void
}
export const useResendFax = ({ onSuccess, onError }: UseResendFaxParams) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: (faxId: number) => api.resendFax({ faxId }),
    onSuccess,
    onError,
  })
}

const pageSize = 20

const useFaxesCache = (queryKey: QueryKey) => {
  const queryClient = useQueryClient()

  // Retrieve cached data for the infinite query
  const getCachedResults = useCallback(() => {
    const cachedData =
      queryClient.getQueryData<InfiniteData<PaginatedApiResult<Fax>, unknown>>(
        queryKey,
      )
    return cachedData?.pages.flatMap((page) => page.results)
  }, [queryKey])

  const get = (itemId) => {
    return getCachedResults()?.find((item) => item.id == itemId)
  }

  const update = (updatedItem: FaxInstance) => {
    queryClient.setQueryData<InfiniteData<PaginatedApiResult<Fax>, unknown>>(
      queryKey,
      (oldData) => {
        if (!oldData) return undefined
        return {
          ...oldData,
          pages: oldData.pages.map((page) => {
            return {
              ...page,
              results: page.results.map((item) =>
                item.id === updatedItem.id ? updatedItem : item,
              ),
            }
          }),
        }
      },
    )
  }

  const remove = (deletedItemId: number) => {
    queryClient.setQueryData<InfiniteData<PaginatedApiResult<Fax>>>(
      queryKey,
      (oldData) => {
        if (!oldData) return undefined
        return {
          ...oldData,
          pages:
            oldData?.pages.map((page) => ({
              ...page,
              results: page.results.filter((item) => item.id !== deletedItemId),
            })) ?? [],
        }
      },
    )
  }

  return {
    get,
    update,
    remove,
  }
}

export function useFaxList({
  search,
  direction,
  extension,
  unread,
  options,
}: FaxSearch) {
  const { pbxId } = useUserState()
  const mangoApi = useMangoApi()

  const queryKey = useMemo(() => {
    const key = [
      'faxes',
      pbxId,
      {
        direction,
        extension,
        search,
        unread,
      },
    ]
    return key
  }, [pbxId, search, direction, extension, unread])

  const cache = useFaxesCache(queryKey)
  const queryFn: QueryFunction<
    PaginatedApiResult<Fax>,
    QueryKey,
    unknown
  > = useCallback(
    async ({ pageParam = 0 }) => {
      const emptyResult = { count: 0, next: null, previous: null, results: [] }
      if (pbxId === 0) {
        return Promise.resolve(emptyResult)
      }
      const offset = (pageParam ?? 0) as number
      const { ok, data } = await mangoApi.getFaxList({
        pbxId,
        offset,
        limit: pageSize,
        direction,
        unread,
        extension_id: extension,
        search,
      })

      if (!ok || !data) {
        return Promise.reject(data as ApiError)
      }

      return data
    },
    [search, direction, extension, unread, options],
  )

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    ...options,
    initialPageParam: 0,
    refetchInterval: fiveMinutesMs,
    retry: (options?.retry as number | boolean | undefined) ?? false,
    getNextPageParam: (lastPage) => {
      if (lastPage.next === null) {
        return null
      }
      return getPaginatedResultParts(lastPage)?.nextPageOffset ?? null
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.previous === null) {
        return null
      }
      return getPaginatedResultParts(firstPage)?.previousPageOffset ?? null
    },
  })

  const faxList = query.data as unknown as InfiniteData<
    PaginatedApiResult<Fax>,
    unknown
  >
  const results = faxList?.pages.flatMap((page) => page.results) ?? []
  const pendingFaxes = results.filter((fax) => fax.status === 'pending')

  // create a useQueries hook to retry pending faxes every minute until they are no longer pending
  useQueries({
    queries: pendingFaxes.map((fax) => ({
      queryKey: [FaxesQueryKeys.FAX, fax.id],
      queryFn: async ({ signal }) => {
        if (!fax.id || fax.id === 0) {
          return Promise.reject({ message: 'faxId is required' })
        }
        const { ok, data, status } = await mangoApi.getFax(fax.id, signal)
        if (data?.status === 'pending') {
          return Promise.reject({ message: 'fax is still pending' })
        }

        if (!ok) {
          const error = {
            ...data,
            status,
          }
          return Promise.reject(error)
        }
        if (ok && data) {
          cache.update(data)
        }
        return data!
      },
      retry: true,
      retryDelay: 60 * 1000,
    })),
  })
  return {
    ...cache,
    ...query,
    faxes: faxList?.pages.flatMap((page) => page.results) ?? [],
  }
}

/** Will automatically mark faxes as read when consumed. */
export function useAutoRead(
  faxId: number | null,
  faxSearch: FaxSearch,
  extensionIds: number[],
) {
  const { data: fax } = useGetFax(faxId)
  const { mutate: markAsRead } = useMarkAsRead(faxId, faxSearch, extensionIds, {
    shouldRehydrateFaxes: false,
  })
  const rehydrateFaxes = useRehydrateFaxes(faxId, faxSearch, extensionIds)

  useEffect(() => {
    if (fax?.read_at === null) {
      markAsRead(fax.id)

      // The timeout fixes an issue when the page mounts with an unread fax selected:
      // The fax gets marked as read, but the styling in the fax list does not capture the update in time
      // if `markAsRead` is responsible for handling the update alone. Since we must rehydrate the faxes
      // at this abstraction layer anyway, we needed a mechanism to skip rehydration where it would usually occur.
      // Without skipping the abstracted rehydration, the unread count would be decremented twice, when it should only be decremented once.
      setTimeout(rehydrateFaxes, autoReadRehydrateTimeoutMs)
    }
  }, [fax?.id])
}

/** @param options.shouldRehydrateFaxes allows the caller to skip rehydrating faxes here should they need to handle that directly */
export function useMarkAsRead(
  faxId: number | null,
  faxSearch: FaxSearch,
  extensionIds: number[],
  options?: {
    shouldRehydrateFaxes?: boolean
  },
) {
  const shouldRehydrateFaxes = options?.shouldRehydrateFaxes ?? true
  const api = useMangoApi()
  const { data: fax } = useGetFax(faxId)
  const rehydrateFaxes = useRehydrateFaxes(faxId, faxSearch, extensionIds)

  return useMutation({
    mutationFn: api.markFaxAsRead,
    onSuccess: (response) => {
      if (!fax) {
        logger.error(
          'TypeError: `fax` is undefined. Only call `mutate` after you validate `fax`.',
        )
        return
      }

      if (!isMarkFaxAsReadResponse(response.data)) {
        logger.error(
          'TypeError: response does not match MarkFaxAsReadResponse. Verify the contract matches typing.',
        )
        return
      }

      if (shouldRehydrateFaxes) {
        rehydrateFaxes({ ...fax, read_at: response.data.read_at })
      }
    },
    onError: (error) => {
      const message = isClientError(error)
        ? error.errors.map((e) => e.detail).join(', ')
        : 'An unexpected error occurred'
      showErrorToast({ message })
    },
  })
}

export function useMarkAsUnread(
  faxId: number | null,
  faxSearch: FaxSearch,
  extensionIds: number[],
) {
  const api = useMangoApi()
  const { data: fax } = useGetFax(faxId)
  const rehydrateFaxes = useRehydrateFaxes(faxId, faxSearch, extensionIds)

  return useMutation({
    mutationFn: api.markFaxAsUnread,
    onSuccess: (_data) => {
      if (!fax) {
        logger.error(
          'TypeError: `fax` is undefined. Only call `mutate` after you validate `fax`.',
        )
        return
      }
      rehydrateFaxes({ ...fax, read_at: null })
    },
    onError: (error) => {
      const message = isClientError(error)
        ? error.errors.map((e) => e.detail).join(', ')
        : 'An unexpected error occurred'
      showErrorToast({ message })
    },
  })
}

/** @description Tracks the count of unread faxes for a given PBX. Automatically refreshes every five minutes. */
export function useUnreadCount() {
  const { pbxId } = useUserState()
  const { getFaxList } = useMangoApi()

  const queryKey = useMemo(() => ['faxUnreadCount', pbxId], [pbxId])

  const queryFn = useCallback(
    async function getFaxUnreadCount() {
      if (pbxId === 0) {
        return Promise.resolve(0)
      }

      const { ok, data } = await getFaxList({
        direction: undefined, // We explicitly don't want to filter count by direction.
        extension_id: undefined, // We explicitly don't want to filter count by extension here. See `useUnreadCountByExtension`.
        limit: 1, // We don't care to get actual records here, just the total count property on the response.
        pbxId,
        unread: true,
      })

      return !ok || !data ? Promise.reject(data as ApiError) : data.count
    },
    [pbxId],
  )

  const query = useQuery({
    queryKey,
    queryFn,
    refetchInterval: fiveMinutesMs,
  })

  return {
    ...query,
    queryKey,
    results: query.data ?? 0,
  }
}

/** Provides a count of unread faxes partitioned by a list of provided extension IDs */
export function useUnreadCountByExtension(extensionIds: number[]) {
  const { pbxId } = useUserState()
  const { getFaxList } = useMangoApi()

  const queryKey = useMemo(
    () => ['faxUnreadCountByExtension', pbxId, extensionIds],
    [pbxId, extensionIds],
  )

  const queryFn = useCallback(
    async function getFaxUnreadCountByExtension() {
      if (pbxId === 0) {
        return new Map<number, number>()
      }

      const responses = await Promise.all(
        extensionIds.map((extensionId) =>
          getFaxList({
            direction: undefined, // We explicitly don't want to filter count by direction.
            extension_id: extensionId,
            limit: 1, // We don't care to get actual records here, just the total count property on the response.
            pbxId,
            unread: true,
          }),
        ),
      )

      let failed = false
      const counts = new Map<number, number>()
      responses.forEach((response, index) => {
        const extensionId = extensionIds[index]

        if (!response.ok || !response.data) {
          failed = true
          return
        }

        counts.set(extensionId, response.data.count)
      })

      if (failed) {
        const details = responses
          .filter((response) => !response.ok)
          .map((response) => response.data)
        return Promise.reject({ details, message: 'Failed to get fax counts' })
      }

      return counts
    },
    [pbxId, extensionIds],
  )

  const query = useQuery({
    queryKey,
    queryFn,
    refetchInterval: fiveMinutesMs,
  })

  return {
    ...query,
    queryKey,
    results: query.data ?? new Map<number, number>(),
  }
}

export function useRehydrateFaxes(
  faxId: number | null,
  faxSearch: FaxSearch,
  extensionIds: number[],
) {
  const fax = useGetFax(faxId)
  const faxList = useFaxList(faxSearch)
  const unreadCount = useUnreadCount()
  const unreadCountByExtension = useUnreadCountByExtension(extensionIds)
  const unreadCountCache = useUnreadCountCache({
    byExtension: unreadCountByExtension.queryKey,
    total: unreadCount.queryKey,
  })

  // `faxUpdated` is optional because an updated fax will not be provided as the timeout callback for the auto-read hook.
  return function rehydrateFaxes(faxUpdated?: FaxInstance) {
    fax.refetch()
    faxList.refetch() // Always refetch the fax list. If the unread filter is applied, updating the fax list cache won't cut it.

    const isFromAutoRead = !faxUpdated

    // Update unread counts. If this was called from `useAutoRead`, not all caches are accessible.
    if (isFromAutoRead) {
      unreadCountCache.decrementTotal()
      unreadCountByExtension.refetch() // The updated fax extension ID is not accessible from this block, so we must refetch the unread counts by extension manually to apply an update instead of relying on the cache.
    } else {
      const unreadDelta = faxUpdated.read_at ? -1 : 1
      unreadCountCache.setExtensionCount(faxUpdated.extension.id, unreadDelta)
    }
  }
}

export function useToggleUnread(
  faxId: number | null,
  faxSearch: FaxSearch,
  extensionIds: number[],
) {
  const { data: fax } = useGetFax(faxId)
  const { mutate: markAsRead } = useMarkAsRead(faxId, faxSearch, extensionIds)
  const { mutate: markAsUnread } = useMarkAsUnread(
    faxId,
    faxSearch,
    extensionIds,
  )

  return function toggleUnread() {
    if (!fax) {
      return
    }

    const toggle = fax.read_at ? markAsUnread : markAsRead

    toggle(fax.id)
  }
}

/**
 * Custom hook to manage the unread count cache for faxes.
 *
 * This hook provides functions to increment and decrement the unread count
 * for specific extensions as well as the total unread count. It ensures that
 * the counts do not go negative and avoids unnecessary cache updates.
 *
 * @param queryKeys - The keys used to query the unread counts.
 *
 * @returns An object containing functions to manage the unread count cache:
 * - `decrementExtension(extensionId: number): void` - Decrements the unread count for a specific extension.
 * - `decrementTotal(): void` - Decrements the total unread count.
 * - `incrementExtension(extensionId: number): void` - Increments the unread count for a specific extension.
 * - `incrementTotal(): void` - Increments the total unread count.
 */
function useUnreadCountCache(queryKeys: UnreadCountQueryKeys) {
  const queryClient = useQueryClient()

  /** Decrements the unread count for a given extension and the total */
  function decrementExtension(extensionId: number) {
    setExtensionCount(extensionId, -1)
  }

  /** Decrements the unread count for the total */
  function decrementTotal() {
    setTotalCount(-1)
  }

  /** Increments the unread count for a given extension and the total */
  function incrementExtension(extensionId: number) {
    setExtensionCount(extensionId, 1)
  }

  /** Increments the unread count for the total */
  function incrementTotal() {
    setTotalCount(1)
  }

  /**
   * @description Updates the count of unread faxes for a given extension, disallowing negative counts and avoiding unnecessary updates.
   * @link https://tanstack.com/query/v5/docs/reference/QueryClient/#queryclientsetquerydata
   */
  function setExtensionCount(extensionId: number, delta: number) {
    queryClient.setQueryData<UnreadExtensionCounts>(
      queryKeys.byExtension,
      (counts = new Map()) => {
        const countsNext = new Map(counts)
        const count = countsNext.get(extensionId) ?? 0
        const countNext = Math.max(count + delta, 0)

        if (count === countNext) {
          return undefined // Returning undefined avoids updating the cache unnecessarily.
        }

        countsNext.set(extensionId, countNext)
        setTotalCount(delta)

        return countsNext
      },
    )
  }

  /**
   * @description Updates the total count of unread faxes, disallowing negative counts and avoiding unnecessary updates.
   * @link https://tanstack.com/query/v5/docs/reference/QueryClient/#queryclientsetquerydata
   */
  function setTotalCount(delta: number) {
    queryClient.setQueryData<number>(queryKeys.total, (count = 0) => {
      const countNext = Math.max(count + delta, 0)

      return countNext === count ? undefined : countNext // Returning undefined avoids updating the cache unnecessarily.
    })
  }

  return {
    decrementExtension,
    decrementTotal,
    incrementExtension,
    incrementTotal,
    setExtensionCount,
    setTotalCount,
  }
}
